import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { paperGeometry, PROCEDURE_HEADER_TOP } from '@atek/eqms-document-layout';

export interface PageMeasurement { pages: number; oversizedBlocks: number; oversizedHeaders: number }
interface PaginationOptions {
  format: 'letter' | 'a4';
  language: 'en' | 'fr' | 'bilingual';
  onMeasure: (measurement: PageMeasurement) => void;
}
const key = new PluginKey<DecorationSet>('eqms-measured-pages');
const GAP = 24;

interface Block { pos: number; dom: HTMLElement; height: number; y: number; section: HTMLElement | null; row: boolean; header: boolean; explicit: boolean }

/** View-only spacers: pages never become editable nodes or change source hashes. */
export const AuthoringPagination = Extension.create<PaginationOptions>({
  name: 'authoringPagination',
  addOptions: () => ({ format: 'letter', language: 'bilingual', onMeasure: () => undefined }),
  addProseMirrorPlugins() {
    const options = this.options;
    return [new Plugin<DecorationSet>({
      key,
      state: {
        init: () => DecorationSet.empty,
        apply: (transaction, decorations) => transaction.getMeta(key) as DecorationSet || decorations.map(transaction.mapping, transaction.doc),
      },
      props: { decorations: state => key.getState(state) },
      view: initialView => {
        let view = initialView; let frame = 0; let signature = ''; let measuring = false; let stopped = false;
        const measure = () => {
          frame = 0; if (stopped || measuring || !view.dom.isConnected) return;
          const paper = view.dom.closest('[data-authoring-paper]');
          if (!(paper instanceof HTMLElement)) return;
          const geometry = paperGeometry(options.format);
          const paperTop = paper.getBoundingClientRect().top;
          const oldWidgets = [...view.dom.querySelectorAll<HTMLElement>('[data-page-break-widget]')].map(dom => ({ y: dom.getBoundingClientRect().top - paperTop, height: dom.getBoundingClientRect().height }));
          const unpagedY = (dom: HTMLElement) => { const y = dom.getBoundingClientRect().top - paperTop; return y - oldWidgets.filter(widget => widget.y < y - 0.5).reduce((sum, widget) => sum + widget.height, 0); };
          const blocks: Block[] = [];
          view.state.doc.descendants((node, pos) => {
            const dom = view.nodeDOM(pos);
            if (!(dom instanceof HTMLElement) || dom.closest('[data-language-hidden="true"]')) return false;
            if (node.type.name === 'procedureSection') {
              const language = node.attrs.language as string;
              if (options.language !== 'bilingual' && options.language !== language) return false;
              const header = dom.querySelector<HTMLElement>('[data-document-header]');
              if (header) blocks.push({ pos, dom: header, height: header.getBoundingClientRect().height, y: unpagedY(header), section: dom, row: false, header: true, explicit: false });
              return true;
            }
            if (node.type.name === 'table') {
              let mergedRows = false; node.descendants(cell => { if (Number(cell.attrs.rowspan || 1) > 1) mergedRows = true; });
              // An inserted spacer row must never consume an author's rowspan.
              if (mergedRows) { blocks.push({ pos, dom, height: dom.getBoundingClientRect().height, y: unpagedY(dom), section: dom.closest('[data-type="procedure-section"]'), row: false, header: false, explicit: false }); return false; }
              return true;
            }
            if (node.type.name === 'orderedList' || node.type.name === 'bulletList') return true;
            if (node.type.name === 'tableRow' || node.type.name === 'listItem' || node.isTextblock || node.isAtom && node.isBlock) {
              blocks.push({ pos, dom, height: dom.getBoundingClientRect().height, y: unpagedY(dom), section: dom.closest('[data-type="procedure-section"]'), row: node.type.name === 'tableRow', header: false, explicit: node.type.name === 'pageBreak' });
              return false;
            }
            return true;
          });
          const decorations: Decoration[] = []; const parts: string[] = []; let added = 0; let lastPage = 0; let oversizedBlocks = 0;
          const insert = (block: Block, at: number, currentY: number, page: number, withHeader: boolean) => {
            const originalHeader = withHeader ? block.section?.querySelector<HTMLElement>('[data-document-header]') || paper.querySelector<HTMLElement>('[data-document-header]') : null;
            const headerHeight = originalHeader ? originalHeader.getBoundingClientRect().height + (originalHeader.hasAttribute('data-header-reservation') ? 0 : 24) : 0;
            const nextTop = (page + 1) * (geometry.height + GAP);
            const height = Math.max(0, nextTop + geometry.margin + headerHeight - currentY);
            if (height < 1) return;
            const sourceRow = originalHeader?.querySelector('.eqms-document-header-row')?.cloneNode(true) as HTMLElement | undefined;
            sourceRow?.querySelectorAll('[data-header-page],[data-header-total]').forEach(element => { element.textContent = ''; });
            const headerSource = sourceRow?.textContent || '';
            parts.push(`${at}:${Math.round(height * 10) / 10}:${page}:${headerHeight}:${headerSource}`);
            decorations.push(Decoration.widget(at, () => {
              const widget = document.createElement(block.row ? 'tr' : 'div');
              widget.setAttribute('data-page-break-widget', ''); widget.setAttribute('data-page-after', String(page + 1));
              widget.contentEditable = 'false'; widget.style.height = `${height}px`; widget.style.position = 'relative';
              widget.style.userSelect = 'none'; widget.style.pointerEvents = 'none'; widget.style.margin = '0'; widget.style.padding = '0';
              const contents = block.row ? document.createElement('td') : widget;
              if (block.row) { (contents as HTMLTableCellElement).colSpan = Math.max(1, [...block.dom.children].reduce((sum, cell) => sum + Number(cell.getAttribute('colspan') || 1), 0)); contents.style.cssText = `height:${height}px;border:0;padding:0;position:relative;background:transparent`; widget.append(contents); }
              if (originalHeader) {
                const header = originalHeader.cloneNode(true) as HTMLElement;
                header.removeAttribute('data-testid'); header.setAttribute('data-repeated-header', '');
                header.querySelectorAll('[data-testid]').forEach(element => element.removeAttribute('data-testid'));
                header.style.cssText = `position:absolute;top:${height - headerHeight}px;left:0;right:0;height:${originalHeader.getBoundingClientRect().height}px;margin:0;pointer-events:none`;
                header.querySelectorAll('[data-header-page]').forEach(element => { element.textContent = String(page + 2); });
                contents.append(header);
              }
              return widget;
            }, { side: -1, key: `page-${at}-${height.toFixed(1)}-${page}-${headerSource}`, ignoreSelection: true }));
            added += height; lastPage = page + 1;
          };
          blocks.forEach((block, index) => {
            const y = block.y + added;
            const page = Math.max(lastPage, Math.floor(y / (geometry.height + GAP)));
            const top = page * (geometry.height + GAP);
            const bottom = top + geometry.height - geometry.margin;
            if (block.header && index > 0) {
              // A selected second language starts on a fresh physical page.
              insert(block, block.pos, y, page, false);
            } else if (block.explicit) {
              insert(block, block.pos, y, page, true);
            } else {
              const header = block.section?.querySelector<HTMLElement>('[data-document-header]');
              const reserved = header ? header.getBoundingClientRect().height + (header.hasAttribute('data-header-reservation') ? 0 : 24) : 0;
              const capacity = geometry.height - geometry.margin * 2 - reserved;
              if (block.height > capacity) oversizedBlocks++;
              let needed = block.height;
              // Keep a heading with the start of the following paragraph or table.
              if (/^H[123]$/.test(block.dom.tagName) && blocks[index + 1]) needed += Math.min(32, blocks[index + 1].height);
              if (y + needed > bottom + 0.5 && y > top + geometry.margin + reserved + 1) insert(block, block.pos, y, page, true);
            }
            lastPage = Math.max(lastPage, Math.floor((block.y + added + block.height - 1) / (geometry.height + GAP)));
          });
          const pages = Math.max(1, lastPage + 1);
          const oversizedHeaders = [...paper.querySelectorAll<HTMLElement>('[data-header-reservation]:not([data-repeated-header])')].filter(header => {
            if (header.closest('[data-language-hidden="true"]')) return false;
            const row = header.querySelector<HTMLElement>('.eqms-document-header-row');
            return Boolean(row && row.getBoundingClientRect().height > Number(header.dataset.headerReservation) - PROCEDURE_HEADER_TOP + 1);
          }).length;
          options.onMeasure({ pages, oversizedBlocks, oversizedHeaders });
          const nextSignature = `${options.format}|${options.language}|${parts.join('|')}`;
          if (nextSignature !== signature) {
            signature = nextSignature; measuring = true;
            view.dispatch(view.state.tr.setMeta(key, DecorationSet.create(view.state.doc, decorations)).setMeta('addToHistory', false));
            measuring = false;
          }
        };
        const schedule = () => { if (!frame && !stopped) frame = requestAnimationFrame(measure); };
        const observer = new ResizeObserver(schedule); observer.observe(view.dom);
        document.fonts.ready.then(schedule).catch(() => undefined);
        view.dom.addEventListener('load', schedule, true); schedule();
        return { update: (nextView: EditorView) => { view = nextView; schedule(); }, destroy: () => { stopped = true; if (frame) cancelAnimationFrame(frame); observer.disconnect(); view.dom.removeEventListener('load', schedule, true); } };
      },
    })];
  },
});
